import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCellPropsSizeOverrides,
  TableContainer,
  TableHead,
  TableRow,
  TableRowTypeMap,
  tableCellClasses
} from "@mui/material";
import TablePaginationComponent from "../TablePagination/TablePagination";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SxProps, Theme, styled } from "@mui/material/styles";
import { OverridableStringUnion } from "@mui/types";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

type TableComponentProps = {
  headerCfg: {
    label: string;
    align?: "inherit" | "left" | "center" | "right" | "justify";
    size?: OverridableStringUnion<
      "small" | "medium",
      TableCellPropsSizeOverrides
    >;
    /**
     * Per-column header styling. Mainly for `whiteSpace: "nowrap"`, which makes
     * the table reserve the width the title needs instead of wrapping it — the
     * columns that should give way are the free-text ones.
     */
    sx?: SxProps<Theme>;
  }[];
  children: any;
  page?: number;
  rowsPerPage?: number;
  total?: number;
  onChangePagination?: (type: "rowsPerPage" | "page", value: number) => void;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    background: "transparent",
    color: semanticColors.text.inverse,
    ...typography.body.lBold,
    height: "48px",
    padding: "0 8px",
    border: "none"
  },
  ...typography.body.l,
  [theme.breakpoints.up("md")]: {
    fontSize: `${typography.body.l.fontSize}!important`
  }
}));

export const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: semanticColors.surface.canvas,
  "& td": {
    borderBottom: `0.5px solid ${semanticColors.border.light}`,
    height: "54px",
    padding: "0 8px",
    ...typography.body.l,
    color: semanticColors.text.primary
  },
  "&:last-child td": {
    borderBottom: "none"
  }
}));

export default function TableComponent({
  headerCfg,
  children,
  page,
  rowsPerPage,
  total,
  onChangePagination
}: TableComponentProps) {
  const havePagination = page && rowsPerPage && total && onChangePagination;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "24px",
            filter: "drop-shadow(0px 0px 16px rgba(30, 39, 79, 0.08))",
            overflow: "hidden",
            boxShadow: "none"
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow sx={{ background: semanticColors.gradient.surface }}>
                {headerCfg.map((cfg, i) => (
                  <StyledTableCell
                    key={`${cfg.label}-${i}`}
                    align={cfg?.align || "center"}
                    size={cfg?.size || "medium"}
                    sx={cfg?.sx}
                  >
                    {cfg.label}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
          </Table>
        </TableContainer>
      </Box>
      {havePagination ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            marginTop: "20px"
          }}
        >
          <TablePaginationComponent
            count={total}
            page={page}
            onPageChange={(value: any) => {
              onChangePagination("page", value);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(value: any) => {
              onChangePagination("rowsPerPage", value);
            }}
          />
        </Box>
      ) : null}
    </>
  );
}
